import apiService from './api';

export type WorkCategoryCode =
  | 'ticket'
  | 'kyc'
  | 'seller_application'
  | 'seller_verification'
  | 'product_approval'
  | 'product_edit'
  | 'store_edit'
  | 'banner_approval'
  | 'user_withdrawal'
  | 'seller_withdrawal'
  | 'dispute'
  | 'article_moderation'
  | 'article_edit'
  | 'article_report'
  | 'bank_verification'
  | 'inspection_penalty'
  | 'review_moderation'
  | 'wholesale_management';

export type AutoAssignStrategy = 'manual' | 'round_robin' | 'least_busy';

export type AdminShiftStatus =
  | 'on_shift'
  | 'off_shift'
  | 'on_break'
  | 'on_leave'
  | 'holiday'
  | 'no_profile'
  | 'inactive';

export interface WorkCategory {
  _id: string;
  code: WorkCategoryCode;
  nameFa: string;
  modelRef: string;
  hasQueue: boolean;
  autoAssignStrategy: AutoAssignStrategy;
  defaultMaxLoad: number;
  isActive: boolean;
}

export interface ShiftPeriod {
  startMinute: number;
  endMinute: number;
}

export interface ShiftDay {
  dayOfWeek: number;
  periods: ShiftPeriod[];
}

export interface ShiftBreak {
  dayOfWeek?: number;
  startMinute: number;
  endMinute: number;
  label?: string;
}

export interface ShiftException {
  _id?: string;
  date: string;
  type: 'leave' | 'extra' | 'holiday';
  periods?: ShiftPeriod[];
  reason?: string;
}

export interface ProfileCategory {
  code: WorkCategoryCode;
  priority: number;
  maxConcurrentLoad: number;
  /** Server bookkeeping for round-robin rotation (read-only on the client). */
  lastAssignedAt?: string | null;
}

export interface AdminWorkProfile {
  _id: string;
  userId: { _id: string; name: string; email: string; isActive: boolean } | string;
  categories: ProfileCategory[];
  shift: {
    timezone: string;
    weeklySchedule: ShiftDay[];
    breaks: ShiftBreak[];
  };
  exceptions: ShiftException[];
  isActive: boolean;
  notes?: string;
  version?: number;
  liveStatus?: AdminShiftStatus;
  createdAt: string;
  updatedAt: string;
}

export interface Holiday {
  _id: string;
  date: string;
  nameFa: string;
  type: 'official' | 'religious' | 'custom';
  isRecurring: boolean;
  jalaliMonth?: number;
  jalaliDay?: number;
  year?: number;
  isActive: boolean;
}

export interface EligibleAdmin {
  _id: string;
  name: string;
  email: string;
  status: AdminShiftStatus;
  isSuperAdmin: boolean;
  priority: number;
  currentLoad: number;
  maxConcurrentLoad: number;
  overloaded: boolean;
  shiftCurrent?: { startMinute: number; endMinute: number; dayOfWeek: number };
  shiftNext?: { startMinute: number; dayOfWeek: number; daysAhead: number };
  minutesUntilShiftEnds?: number;
}

export interface ShiftWindow {
  current?: { startMinute: number; endMinute: number; dayOfWeek: number };
  next?: { startMinute: number; dayOfWeek: number; daysAhead: number };
}

/** One work item assigned to the current admin, normalized across ~17 domain models. */
export interface MyTaskItem {
  id: string;
  category: WorkCategoryCode;
  categoryNameFa: string;
  title: string;
  subtitle?: string;
  /** Raw domain status — each model has its own enum. */
  status: string;
  isOpen: boolean;
  amount?: number;
  assignedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  ageHours: number;
  isStale: boolean;
  /** Admin-panel relative path for this item. */
  link: string;
}

export interface MyTasksResponse {
  items: MyTaskItem[];
  meta: { page: number; limit: number; totalItems: number; totalPages: number };
  counts: { category: WorkCategoryCode; nameFa: string; open: number; total: number }[];
  summary: { open: number; total: number; stale: number; categories: number };
  myCategories: WorkCategoryCode[];
  /** Categories whose result hit the server-side cap — that list is incomplete. */
  truncated: WorkCategoryCode[];
}

export interface MyTasksQuery {
  status?: 'open' | 'all';
  category?: WorkCategoryCode | '';
  sort?: 'oldest' | 'newest';
  page?: number;
  limit?: number;
}

const BASE = '/admin/work-assignment';

const workAssignmentService = {
  /* Categories */
  listCategories: () => apiService.get<WorkCategory[]>(`${BASE}/categories`),
  updateCategory: (code: string, data: Partial<WorkCategory>) =>
    apiService.put<WorkCategory>(`${BASE}/categories/${code}`, data),

  /* Profiles */
  listProfiles: (params: { page?: number; limit?: number; search?: string } = {}) =>
    apiService.get<any>(`${BASE}/profiles`, { params }),
  getMyStatus: () =>
    apiService.get<{
      liveStatus: AdminShiftStatus;
      profile: AdminWorkProfile | null;
      shiftWindow: ShiftWindow;
    }>(`${BASE}/profiles/me`),
  getProfileByUser: (userId: string) =>
    apiService.get<AdminWorkProfile>(`${BASE}/profiles/${userId}`),

  /* My tasks — always scoped to the caller server-side; no userId param exists */
  getMyTasks: (query: MyTasksQuery = {}) => {
    const params: Record<string, string> = {
      status: query.status || 'open',
      sort: query.sort || 'oldest',
      page: String(query.page || 1),
      limit: String(query.limit || 20),
    };
    if (query.category) params.category = query.category;
    return apiService.get<MyTasksResponse>(`${BASE}/my-tasks`, { params });
  },
  upsertProfile: (data: {
    userId: string;
    categories: ProfileCategory[];
    shift: { weeklySchedule: ShiftDay[]; breaks: ShiftBreak[] };
    exceptions?: ShiftException[];
    isActive?: boolean;
    notes?: string;
    version?: number;
  }) => apiService.post<AdminWorkProfile>(`${BASE}/profiles`, data),
  deleteProfile: (userId: string) => apiService.delete(`${BASE}/profiles/${userId}`),
  addException: (userId: string, exception: ShiftException) =>
    apiService.post<AdminWorkProfile>(`${BASE}/profiles/${userId}/exceptions`, exception),

  /* Eligible admins (unified) */
  getEligibleAdmins: (category: WorkCategoryCode, at?: string) =>
    apiService.get<{ admins: EligibleAdmin[]; at: string }>(`${BASE}/eligible-admins`, {
      params: { category, at },
    }),

  /* Admin user picker */
  listAdminUsers: () =>
    apiService.get<Array<{ _id: string; name: string; email: string; isSuperAdmin: boolean }>>(
      `${BASE}/admin-users`
    ),

  /* Holidays */
  listHolidays: () => apiService.get<Holiday[]>(`${BASE}/holidays`),
  createHoliday: (data: Partial<Holiday>) =>
    apiService.post<Holiday>(`${BASE}/holidays`, data),
  updateHoliday: (id: string, data: Partial<Holiday>) =>
    apiService.put<Holiday>(`${BASE}/holidays/${id}`, data),
  deleteHoliday: (id: string) => apiService.delete(`${BASE}/holidays/${id}`),
};

export const CATEGORY_LABELS: Record<WorkCategoryCode, string> = {
  ticket: 'تیکت‌های پشتیبانی',
  kyc: 'احراز هویت (KYC)',
  seller_application: 'درخواست فروشندگی',
  seller_verification: 'احراز فروشنده',
  product_approval: 'تایید محصولات',
  product_edit: 'ویرایش محصولات',
  store_edit: 'ویرایش فروشگاه‌ها',
  banner_approval: 'تایید بنرها',
  user_withdrawal: 'برداشت کاربران',
  seller_withdrawal: 'برداشت فروشندگان',
  dispute: 'داوری اختلافات',
  article_moderation: 'مدیریت مقالات',
  article_edit: 'ویرایش مقالات',
  article_report: 'گزارش مقالات',
  bank_verification: 'تایید حساب بانکی',
  inspection_penalty: 'جریمه‌های بازرسی',
  review_moderation: 'داوری نظرات',
  wholesale_management: 'مدیریت عمده‌فروشی',
};

export const STATUS_LABELS: Record<AdminShiftStatus, { label: string; color: string }> = {
  on_shift: { label: 'در شیفت', color: 'success' },
  off_shift: { label: 'خارج از شیفت', color: 'default' },
  on_break: { label: 'استراحت', color: 'warning' },
  on_leave: { label: 'مرخصی', color: 'info' },
  holiday: { label: 'تعطیل', color: 'info' },
  no_profile: { label: 'بدون پروفایل', color: 'default' },
  inactive: { label: 'غیرفعال', color: 'error' },
};

export const DAYS_OF_WEEK_FA = [
  'شنبه',
  'یک‌شنبه',
  'دوشنبه',
  'سه‌شنبه',
  'چهارشنبه',
  'پنج‌شنبه',
  'جمعه',
];

/** Format a minute value (0..2880) as HH:MM; values > 1440 wrap ("02:00 فردا"). */
export function formatMinuteFa(m: number): string {
  const dayTag = m >= 1440 ? ' (فردا)' : '';
  const clamped = m % 1440;
  const h = Math.floor(clamped / 60);
  const mm = clamped % 60;
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}${dayTag}`;
}

/** Summarize a weekly schedule as a human string like "شنبه ۰۸:۰۰–۱۶:۰۰ · یک‌شنبه ۰۸:۰۰–۱۶:۰۰". */
export function summarizeWeekly(
  weekly: { dayOfWeek: number; periods: { startMinute: number; endMinute: number }[] }[] | undefined
): string {
  if (!weekly || weekly.length === 0) return 'بدون شیفت';
  const parts: string[] = [];
  const sorted = [...weekly].sort((a, b) => a.dayOfWeek - b.dayOfWeek);
  for (const d of sorted) {
    if (!d.periods || d.periods.length === 0) continue;
    const ranges = d.periods
      .slice()
      .sort((a, b) => a.startMinute - b.startMinute)
      .map((p) => `${formatMinuteFa(p.startMinute)}–${formatMinuteFa(p.endMinute)}`)
      .join(' ، ');
    parts.push(`${DAYS_OF_WEEK_FA[d.dayOfWeek]}: ${ranges}`);
  }
  return parts.length ? parts.join(' · ') : 'بدون شیفت';
}

export default workAssignmentService;
